import { Link } from 'react-router-dom';
import {
  FileCheck,
  Award,
  Brain,
  Smartphone,
  Lock,
  ClipboardCheck,
  Truck,
  QrCode,
  ArrowRight,
  CheckCircle2,
  Menu,
  X,
  BookOpen,
  Settings,
  Users
} from 'lucide-react';

export function LandingPage() {
  return (
    <div className="overflow-y-auto bg-white text-gray-900">
      {/* NAV */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Conformara logo" className="h-8 w-8" />
            <span className="text-lg font-black tracking-tight">Conformara</span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-600">
            <a href="#platform" className="hover:text-gray-900">Platform</a>
            <a href="#modules" className="hover:text-gray-900">Modules</a>
            <a href="#dvir" className="hover:text-gray-900">DOT</a>
            <a href="#pricing" className="hover:text-gray-900">Pricing</a>
            <a href="#how" className="hover:text-gray-900">How it works</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-semibold text-gray-700 hover:text-gray-900">
              Sign in
            </Link>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition"
            >
              Start free trial
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-20 md:py-28">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-400/30 text-blue-200 text-xs font-semibold mb-6 mb-8">
              <Lock className="h-3.5 w-3.5" />
              Field safety, compliance & operations — in one place
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black leading-tight mb-6">
              Safety and compliance software built for the field
            </h1>
            <p className="text-lg md:text-xl text-blue-200 font-light mb-10 max-w-2xl mx-auto">
              Job hazard analysis, audits, certifications, equipment tracking, and DOT inspections.
              Designed to be used at the jobsite, not chained to a desk.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-900 font-bold rounded-lg hover:bg-gray-100 transition"
              >
                Start free trial
                <ArrowRight className="h-5 w-5" />
              </Link>
              <a
                href="/demo"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500/20 text-white font-semibold rounded-lg hover:bg-blue-500/30 transition"
              >
                Explore live demo
              </a>
            </div>
            <p className="text-sm text-blue-300 mt-6">
              No credit card required. Set up in minutes.
            </p>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-b border-gray-200 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-gray-500">
          <span className="inline-flex items-center gap-2"><Lock className="h-4 w-4" /> Your data stays yours</span>
          <span className="inline-flex items-center gap-2"><Smartphone className="h-4 w-4" /> Works on any phone</span>
          <span className="inline-flex items-center gap-2"><QrCode className="h-4 w-4" /> Instant field verification</span>
          <span className="inline-flex items-center gap-2"><Truck className="h-4 w-4" /> Built for multi-vehicle fleets</span>
        </div>
      </section>

      {/* PLATFORM INTRO */}
      <section id="platform" className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl md:text-4xl font-black mb-4">
              Stop running safety across five different tools
            </h2>
            <p className="text-lg text-gray-600">
              Most crews juggle paper JHA forms, spreadsheets, a separate LMS, and clunky audit tools. 
              Conformara brings the whole picture into one mobile-first workflow.
            </p>
          </div>

          <div id="modules" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition">
              <ClipboardCheck className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold text-lg mb-2">Job Hazard Analysis</h3>
              <p className="text-sm text-gray-600">Smart, mobile-first JHA forms your crew can complete in minutes — from the jobsite, not the office.</p>
            </div>
            <div className="rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition">
              <FileCheck className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold text-lg mb-2">Audits & Findings</h3>
              <p className="text-sm text-gray-600">Photo-backed inspections with a clear path to resolution. A finding is never left as just a checkbox.</p>
            </div>
            <div className="rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition">
              <Award className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold text-lg mb-2">Certifications</h3>
              <p className="text-sm text-gray-600">Track training and certs across providers, with a QR code your auditors can scan right at the gate.</p>
            </div>
            <div className="rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition">
              <Settings className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold text-lg mb-2">Asset & Equipment</h3>
              <p className="text-sm text-gray-600">Full lifecycle tracking with maintenance logs — know what you own and when it needs attention.</p>
            </div>
            <div className="rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition">
              <Truck className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold text-lg mb-2">DOT Vehicle Inspections</h3>
              <p className="text-sm text-gray-600">eDVIR pre-trip and post-trip logs with tamper-evident records, photo evidence, and repair tracking.</p>
            </div>
            <div className="rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition">
              <Brain className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold text-lg mb-2">Private AI Assistant</h3>
              <p className="text-sm text-gray-600">An assistant trained on your policies and standards — not a public chatbot. Your data stays yours.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold mb-4">
              Simple, transparent pricing
            </div>
            <h2 className="text-3xl md:text-4xl font-black mb-4">
              One platform. A price you can read without a sales call.
            </h2>
            <p className="text-lg text-gray-600">
              Built around real tower crews — 4 people, one foreman. Start free for
              14 days, no credit card. Scale by the crew, not by hidden per-seat markup.
            </p>
          </div>

          {/* Tiers */}
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {/* Small Business Suite */}
            <div className="rounded-2xl border border-gray-200 p-6 flex flex-col">
              <h3 className="font-black text-lg mb-1">Small Business Suite</h3>
              <p className="text-sm text-gray-500 mb-5">For the growing field operation</p>
              <div className="text-4xl font-black text-gray-900 mb-1">$150<span className="text-base font-semibold text-gray-500">/mo</span></div>
              <p className="text-xs text-gray-500 mb-5">14-day free trial · no card required</p>
              <ul className="space-y-2.5 text-sm text-gray-700 mb-6 flex-1">
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" /> <span>3 field crews (3 foremen + 9 workers)</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" /> <span>Seat for every role — including Safety</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" /> <span>8 vehicles · unlimited trailers & projects</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" /> <span>All modules: JHA, audits, incidents, certs, PPE, DOT, assets</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" /> <span>Add a crew for $50/mo (up to 2 add-ons)</span></li>
              </ul>
              <Link to="/signup" className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition">
                Start free trial <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Large Company */}
            <div className="rounded-2xl border-2 border-blue-600 bg-gradient-to-b from-blue-50 to-white p-6 flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">MOST POPULAR</div>
              <h3 className="font-black text-lg mb-1">Large Company</h3>
              <p className="text-sm text-gray-500 mb-5">Multi-crew operations & full fleets</p>
              <div className="text-4xl font-black text-blue-700 mb-1">$397<span className="text-base font-semibold text-gray-500">/mo</span></div>
              <p className="text-xs text-gray-500 mb-5">14-day free trial · no card required</p>
              <ul className="space-y-2.5 text-sm text-gray-700 mb-6 flex-1">
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" /> <span>12 field crews (~55 staff seats)</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" /> <span>Unlimited vehicles & trailers</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" /> <span>Unlimited projects</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" /> <span>Everything in Small Business Suite</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" /> <span>Sub-accounts & AI visibility reporting</span></li>
              </ul>
              <Link to="/signup" className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-blue-700 text-white font-bold rounded-lg hover:bg-blue-800 transition">
                Start free trial <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Corporate */}
            <div className="rounded-2xl border border-gray-200 p-6 flex flex-col">
              <h3 className="font-black text-lg mb-1">Corporate</h3>
              <p className="text-sm text-gray-500 mb-5">Enterprise-scale, tailored</p>
              <div className="text-4xl font-black text-gray-900 mb-1">$900<span className="text-base font-semibold text-gray-500">/mo+</span></div>
              <p className="text-xs text-gray-500 mb-5">Custom configuration · priority support</p>
              <ul className="space-y-2.5 text-sm text-gray-700 mb-6 flex-1">
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" /> <span>Unlimited crews, staff, vehicles & projects</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" /> <span>Custom software & integrations</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" /> <span>Needs-gathering consult + priority support</span></li>
              </ul>
              <Link to="/signup" className="inline-flex items-center justify-center gap-2 px-5 py-3 border-2 border-blue-600 text-blue-700 font-bold rounded-lg hover:bg-blue-50 transition">
                Start free trial <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Footnote */}
          <p className="text-center text-sm text-gray-500 mt-8">
            Unlimited trailers and projects on every tier, never counted against your vehicles.
            Need a custom plan? We&apos;ll scope it with you — no sales pressure.
          </p>
        </div>
      </section>

      {/* DOT DEEPDIVE */}
      <section id="dvir" className="py-16 md:py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-400/30 text-blue-200 text-xs font-semibold mb-4">
              <Truck className="h-3.5 w-3.5" />
              DOT / FMCSA
            </div>
            <h2 className="text-3xl md:text-4xl font-black mb-4">
              DVIRs without the paper booklet
            </h2>
            <p className="text-lg text-blue-200 font-light mb-6">
              Replace the lost, smeared, late-filed paper log with a digital pre-trip / post-trip inspection that carries real evidence.
            </p>
            <ul className="space-y-3 text-blue-100">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5 shrink-0" />
                Pre-trip and post-trip logs with tamper-evident, server-stamped timestamps
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5 shrink-0" />
                Photo evidence per question — tire tread depth gauges, four-corner exterior, repair proof
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5 shrink-0" />
                Defects that must reach a resolution — not a box that gets checked and forgotten
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5 shrink-0" />
                Works for anything from a pickup to a semi pulling a trailer to a mobile crane
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-blue-400/20 bg-white/5 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Smartphone className="h-5 w-5 text-blue-300" />
              </div>
              <div>
                <div className="font-bold">Pre-trip checklist</div>
                <div className="text-xs text-blue-300">Example · bucket truck</div>
              </div>
            </div>
            <div className="space-y-3">
              {[
                ['Brakes', 'Air loss test'],
                ['Steering', 'Travel, free play'],
                ['Tires — FL', 'Tread gauge + photo'],
                ['Tires — FR', 'Tread gauge + photo'],
                ['Tires — R1-L', 'Tread gauge + photo (dually)'],
                ['Exterior', 'Four-corner diagonal photos'],
              ].map(([q, note]) => (
                <div key={q} className="flex items-center justify-between rounded-lg bg-white/10 px-4 py-3">
                  <div>
                    <div className="font-semibold text-sm">{q}</div>
                    <div className="text-xs text-blue-300">{note}</div>
                  </div>
                  <span className="text-xs font-semibold text-green-400 border border-green-400/40 rounded-full px-3 py-1">Pass</span>
                </div>
              ))}
            </div>
            <div className="mt-5 text-center text-xs text-blue-300">
              Every question supports camera + gallery evidence
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl md:text-4xl font-black mb-4">Set up in a day. Live in the field tomorrow.</h2>
            <p className="text-lg text-gray-600">Your crew already works on their phones. This plugs in without a rollout marathon.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-5xl font-black text-blue-600 mb-3">1</div>
              <h3 className="font-bold text-lg mb-2">Import your team & vehicles</h3>
              <p className="text-sm text-gray-600">Drivers, crews, and vehicles — including your multi-axle and trailer configs.</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black text-blue-600 mb-3">2</div>
              <h3 className="font-bold text-lg mb-2">Configure your checklists</h3>
              <p className="text-sm text-gray-600">Start from built-in templates, then add, edit, or remove questions for your business. Link standards right to each one.</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black text-blue-600 mb-3">3</div>
              <h3 className="font-bold text-lg mb-2">Work in the field</h3>
              <p className="text-sm text-gray-600">JHA, audits, certs, and DVIRs from any phone — with evidence, timestamps, and resolution tracking.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRIVACY */}
      <section className="py-16 md:py-20 bg-gray-50 border-y border-gray-200">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <Lock className="h-10 w-10 text-blue-600 mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-black mb-4">Private by design</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Conformara is built so your data stays under your control. The AI assistant works from 
            your own policies and standards — your records aren't feeding a public model, and 
            nothing about your operation is used to train someone else's product.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-600 to-sky-600 text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-black mb-4">Ready to see it on your jobsite?</h2>
          <p className="text-lg md:text-xl opacity-90 mb-8">
            Start your free trial. No card required, no lock-in.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-gray-100 transition text-lg shadow-2xl"
            >
              Start free trial
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
          <p className="text-sm opacity-75 mt-6">
            Questions? Reach out — we're happy to walk you through it.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 bg-slate-900 text-gray-400">
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Conformara logo" className="h-6 w-6" />
            <span className="font-bold text-white">Conformara</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="#platform" className="hover:text-white">Platform</a>
            <a href="#modules" className="hover:text-white">Modules</a>
            <a href="#dvir" className="hover:text-white">DOT</a>
            <a href="mailto:echobravotango@agentmail.to?subject=Conformara%20support" className="hover:text-white">Support</a>
            <Link to="/login" className="hover:text-white">Sign in</Link>
          </div>
          <div className="text-xs">© {new Date().getFullYear()} Conformara. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}